/**
 * SlotMachine — Cyber Slot Machine Engine (3 Reels).
 *
 * Rules:
 * - Cost: 1 Slot-Machine-Ticket per spin.
 * - Reel 1: All active playable units, PLUS one special "Spieler" (Player /
 *   Account) entry for the global Card Frame category.
 * - Reel 2: The cosmetic attribute type - per-unit ('bullet_color',
 *   'unit_aura', 'rocket_trail') or account-wide ('frame', 'hp_bar_color',
 *   'avatar_border').
 * - Reel 3: 24 styles (REEL_3_STYLES) - the shared color/style palette all
 *   attribute types draw from, EXCEPT 'taunt': a taunt has no color-swatch
 *   equivalent, so that attribute resolves its own prize independently from
 *   LOCKABLE_TAUNT_IDS instead of from styleIndex/REEL_3_STYLES - see the
 *   'taunt' branch in spin(). The reel still visually spins to some color
 *   (cosmetic only in that case); the actual taunt won is read from
 *   result.taunt, not result.style.
 * - Reel 1 and Reel 2 are coupled, not independent: a unit can only land
 *   with a per-unit attribute, and "Spieler" only with an account-wide one
 *   - see _pickCategory() and _unitAttributesFor(). A unit-shaped result
 *   paired with the frame attribute (or vice versa) would be meaningless,
 *   since frames are equipped account-wide while bullet colors, auras and
 *   rocket trails are equipped per unit.
 * - Duplicates: If the player already owns the combination, they are refunded 100 Fugazi immediately!
 */

import { CARDS_DATA, getProfileArtworkCards } from '../data/cards.js';
import { wallet } from './wallet.js';
import { inventory, isBulletColorExcluded, isRocketTrailUnit } from './inventory.js';
import { getTauntById, LOCKABLE_TAUNT_IDS } from '../data/taunts.js';
import { DECKBUILDER_THEMES } from '../data/deckbuilderThemes.js';
import { CARD_THEMES } from '../data/cardThemes.js';
import { DECK_ICON_PRESETS } from '../data/customDecks.js';
import { getSlotCategorySvg } from '../ui/slotIcons.js';

export const SPIN_COST_TICKETS = 1;
export const DUPLICATE_REFUND_FUGAZI = 100;

// Harmonized Hybrid Distribution (Option 1):
// Fixed odds of landing the 'player' category on any given spin (30% player, 70% unit).
// Within both pools, all individual items are mathematically equalized:
// - All 57 unit slots (21x2 non-rocket + 5x3 rocket) have equal odds, eliminating the rocket penalty.
// - All 185 player items (frames, colors, taunts, artworks, themes, icons) have equal odds (~0.162% each).
export const PLAYER_CATEGORY_PROBABILITY = 0.30;
export const FRAME_CATEGORY_PROBABILITY = PLAYER_CATEGORY_PROBABILITY;

// Reel 1's pseudo-entry for the global Player categories. Shaped enough
// like a unit-pool entry (name/image) for the reel UI to render it in the
// same list, but carries no unit identity.
export const PLAYER_REEL_ENTRY = { id: 'player', name: 'Player (Global)', icon: getSlotCategorySvg('player', 'reel'), isPlayer: true };

export const REEL_2_ATTRIBUTES = [
  { id: 'bullet_color',  name: 'Bullet Color',   icon: getSlotCategorySvg('bullet_color', 'reel'),   description: 'Projectile & Laser Color', target: 'unit' },
  { id: 'unit_aura',     name: 'Unit Aura',      icon: getSlotCategorySvg('unit_aura', 'reel'),      description: 'Ground Aura & Underglow',   target: 'unit' },
  { id: 'frame',         name: 'Card Frame',     icon: getSlotCategorySvg('frame', 'reel'),          description: 'Global frame around cards', target: 'player' },
  { id: 'hp_bar_color',  name: 'HP Bar Color',   icon: getSlotCategorySvg('hp_bar_color', 'reel'),   description: 'Player HP Bar Neon Glow',   target: 'player' },
  { id: 'avatar_border', name: 'Avatar Border',  icon: getSlotCategorySvg('avatar_border', 'reel'),  description: 'Player Avatar Aura Glow',  target: 'player' },
  { id: 'tray_color',    name: 'Card Tray Glow', icon: getSlotCategorySvg('tray_color', 'reel'),     description: 'Recessed Hand Tray Background', target: 'player' },
  { id: 'deck_color',    name: 'Deck Color',     icon: getSlotCategorySvg('deck_color', 'reel'),     description: 'Deck Theme & Selection Glow', target: 'player' },
  // Rocket trails are per-unit like bullet colors and auras, but they only
  // exist for rocket-propelled units - see _unitAttributesFor().
  { id: 'rocket_trail',  name: 'Rocket Trail',   icon: getSlotCategorySvg('rocket_trail', 'reel'),   description: 'Rocket Exhaust & Smoke Trail', target: 'unit' },
  // Commander Taunt: account-wide like frame/hp_bar_color/avatar_border/
  // tray_color, but a spin never auto-equips it (see spin() below) - a
  // human player's 3-taunt loadout is chosen explicitly in the Arsenal's
  // Master -> Taunts tab (js/ui/showroomModal.js), not decided by RNG.
  { id: 'taunt',         name: 'Commander Taunt',icon: getSlotCategorySvg('taunt', 'reel'),          description: 'Commander Taunt / Emote Phrase', target: 'player' },
  // Profile Artwork: commander avatar card artworks, chosen from active cards with artwork
  { id: 'profile_artwork', name: 'Profile Artwork', icon: getSlotCategorySvg('profile_artwork', 'reel'), description: 'Commander Profile Artwork', target: 'player' },
  // Deckbuilder: global card chassis theme (e.g. Classic Steel unlockable)
  { id: 'deckbuilder_card', name: 'Deck Builder', icon: getSlotCategorySvg('deckbuilder_card', 'reel'), description: 'Deck Builder Card Chassis Theme', target: 'player' },
  // Cards: in-game hand card chassis theme (Cyber Glass, Fluted, Holo, Classic Steel, Artwork)
  { id: 'card_theme', name: 'Cards', icon: getSlotCategorySvg('card_theme', 'reel'), description: 'In-Game Hand Card Chassis Theme', target: 'player' },
  // Deck Icon: global cosmetic & custom deck insignia (7 unlockable masterwork crests)
  { id: 'deck_icon', name: 'Deck Icon', icon: getSlotCategorySvg('deck_icon', 'reel'), description: 'Deck Insignia & Crest', target: 'player' }
];

export const REEL_3_STYLES = [
  { id: 'electric_cyan',  name: 'Electric Cyan',  hex: '#00F0FF', category: 'Plasma' },
  { id: 'acid_volt',      name: 'Acid Volt',      hex: '#39FF14', category: 'Plasma' },
  { id: 'void_violet',    name: 'Void Violet',    hex: '#8B5CF6', category: 'Plasma' },
  { id: 'pulse_crimson',  name: 'Pulse Crimson',  hex: '#E11D48', category: 'Plasma' },
  { id: 'chrome_silver',  name: 'Chrome Silver',  hex: '#E2E8F0', category: 'Metallic' },
  { id: 'obsidian_black', name: 'Obsidian Black', hex: '#0F172A', category: 'Metallic' },
  { id: 'empire_gold',    name: 'Empire Gold',    hex: '#F59E0B', category: 'Metallic' },
  { id: 'hazard_yellow',  name: 'Hazard Yellow',  hex: '#FACC15', category: 'Industrial' },
  { id: 'cryo_frost',     name: 'Cryo Frost',     hex: '#38BDF8', category: 'Elemental' },
  { id: 'magma_rust',     name: 'Magma Rust',     hex: '#EA580C', category: 'Industrial' },
  { id: 'nebula_coral',   name: 'Nebula Coral',   hex: '#FB7185', category: 'Exotic' },
  { id: 'deep_space_blue',name: 'Deep Space Blue',hex: '#1E3A8A', category: 'Metallic' },
  { id: 'ion_mint',       name: 'Ion Mint',       hex: '#14B8A6', category: 'Elemental' },
  { id: 'beryllium_bronze',name:'Beryllium Bronze',hex: '#B45309', category: 'Industrial' },
  { id: 'tactical_olive',     name: 'Tactical Olive',     hex: '#5D7036', category: 'Industrial' },
  { id: 'cyber_pink',         name: 'Cyber Pink',         hex: '#FF2A85', category: 'Plasma' },
  { id: 'stealth_anthracite', name: 'Stealth Anthracite', hex: '#282C34', category: 'Metallic' },
  { id: 'battleship_grey',    name: 'Battleship Grey',    hex: '#64748B', category: 'Metallic' },
  { id: 'supernova_white',    name: 'Supernova White',    hex: '#F8FAFC', category: 'Plasma' },
  { id: 'toxic_emerald',      name: 'Toxic Emerald',      hex: '#059669', category: 'Elemental' },
  { id: 'cobalt_strike',      name: 'Cobalt Strike',      hex: '#2563EB', category: 'Plasma' },
  { id: 'abyssal_red',        name: 'Abyssal Red',        hex: '#991B1B', category: 'Industrial' },
  { id: 'royal_amethyst',     name: 'Royal Amethyst',     hex: '#7C3AED', category: 'Exotic' },
  { id: 'solar_flare',        name: 'Solar Flare',        hex: '#FF7A00', category: 'Elemental' }
];

export const REEL_3_STYLES_MAP = new Map(REEL_3_STYLES.map(s => [s.id, s]));

class SlotMachineManager {
  constructor() {
    this._unitsPool = null;
    this._unitSlots = null;
  }

  getUnitsPool() {
    if (!this._unitsPool) {
      this._unitsPool = CARDS_DATA
        .filter(c => (c.enabled === true || c.type === 'Melt') && !c.isCampaignOnly && !c.isSpell && c.type !== 'Spell' && !c.isModule && c.type !== 'Module' && !isBulletColorExcluded(c.name))
        .map(c => ({
          name: c.name,
          image: c.images?.[0] || 'card_marine.png',
          rarity: c.rarity || 'Common',
          cost: c.cost ?? 15
        }));
    }
    return this._unitsPool;
  }

  // Reel 1's full display list: every unit, then the "Spieler" entry for the
  // frame category, in that fixed order - PLAYER_REEL_ENTRY's index is
  // therefore always units.length, however many units the roster holds.
  getReel1Items() {
    if (!this._reel1Items) {
      this._reel1Items = [...this.getUnitsPool(), PLAYER_REEL_ENTRY];
    }
    return this._reel1Items;
  }

  canSpin() {
    return wallet.getTickets() >= SPIN_COST_TICKETS;
  }

  // Decides which cosmetic category this spin resolves to. Kept separate
  // from the reel-1/reel-2 index math below so the odds stay a single,
  // easy-to-audit constant (FRAME_CATEGORY_PROBABILITY) instead of being
  // implicit in pool sizes that change as the roster grows.
  _pickCategory() {
    return Math.random() < FRAME_CATEGORY_PROBABILITY ? 'player' : 'unit';
  }

  // Which per-unit cosmetics this particular unit can win. Every unit has a
  // bullet color and an aura, but only rocket-propelled units (Rocketship,
  // Orchestra, ...) have an exhaust plume, so a Marine x Rocket Trail combo
  // must never come up - it would be unequippable and unrenderable.
  _unitAttributesFor(unitName) {
    return REEL_2_ATTRIBUTES.filter(a =>
      a.target === 'unit' && (a.id !== 'rocket_trail' || isRocketTrailUnit(unitName))
    );
  }

  // Flat list of all 59 available (Unit x Attribute) slots across the roster.
  // 22 standard units have 2 attributes (bullet_color, unit_aura) = 44 slots.
  // 5 rocket-propelled units have 3 (+ rocket_trail) = 15 slots.
  // Picking uniformly from these 59 slots guarantees that all unit cosmetic
  // options have the exact same drop probability, eliminating the penalty
  // where rocket units had 33% rarer bullet colors and auras.
  getUnitSlots() {
    if (!this._unitSlots) {
      const slots = [];
      const units = this.getUnitsPool();
      units.forEach((unit, unitIndex) => {
        const attrs = this._unitAttributesFor(unit.name);
        attrs.forEach((attr) => {
          slots.push({ unit, unitIndex, attribute: attr });
        });
      });
      this._unitSlots = slots;
    }
    return this._unitSlots;
  }

  // Exact item count in each Player/Global cosmetic pool (total 185 items):
  // 5 color-based categories x 24 styles = 120 items
  // + 5 taunts + 45 artworks + 4 deckbuilder themes + 4 card themes + 7 deck icons = 65 items.
  getPlayerAttributePoolSizes() {
    return {
      frame: REEL_3_STYLES.length,
      hp_bar_color: REEL_3_STYLES.length,
      avatar_border: REEL_3_STYLES.length,
      tray_color: REEL_3_STYLES.length,
      deck_color: REEL_3_STYLES.length,
      taunt: LOCKABLE_TAUNT_IDS.length,
      profile_artwork: getProfileArtworkCards().length,
      deckbuilder_card: DECKBUILDER_THEMES.filter(t => t.surgeReward).length,
      card_theme: CARD_THEMES.filter(t => t.surgeReward).length,
      deck_icon: DECK_ICON_PRESETS.filter(p => p.surgeReward).length
    };
  }

  // Cumulative ranges across the 185 player items for deterministic testing and auditing.
  getPlayerAttributeCumulativeRanges() {
    const playerAttrs = REEL_2_ATTRIBUTES.filter(a => a.target === 'player');
    const poolSizes = this.getPlayerAttributePoolSizes();
    const ranges = new Map();
    let current = 0;
    for (const attr of playerAttrs) {
      const size = poolSizes[attr.id] || 0;
      ranges.set(attr.id, {
        start: current,
        end: current + size,
        size,
        midpoint: current + size / 2
      });
      current += size;
    }
    return { ranges, totalItems: current };
  }

  // Picks a player attribute weighted by the size of its item pool.
  // Because P(Attr) = Size / 185 and P(Item in Attr) = 1 / Size, the pool size
  // cancels out completely, ensuring EVERY single player item has an exact
  // 1 / 185 probability within the player drop pool (~0.162% overall).
  _pickPlayerAttribute() {
    const playerAttrs = REEL_2_ATTRIBUTES.filter(a => a.target === 'player');
    const { ranges, totalItems } = this.getPlayerAttributeCumulativeRanges();
    const roll = Math.random() * totalItems;
    for (const attr of playerAttrs) {
      const r = ranges.get(attr.id);
      if (roll < r.end) {
        return attr;
      }
    }
    return playerAttrs[playerAttrs.length - 1];
  }

  spin() {
    if (wallet.getTickets() < SPIN_COST_TICKETS) {
      return {
        success: false,
        reason: 'insufficient_tickets',
        availableTickets: wallet.getTickets(),
        requiredTickets: SPIN_COST_TICKETS
      };
    }

    const spent = wallet.spendTickets(SPIN_COST_TICKETS, 'slot_spin');
    if (!spent) {
      return {
        success: false,
        reason: 'transaction_failed'
      };
    }

    const units = this.getUnitsPool();
    const category = this._pickCategory();

    const styleIndex = Math.floor(Math.random() * REEL_3_STYLES.length);
    const style = REEL_3_STYLES[styleIndex];

    let reel1Index, reel1Item, attribute, isDuplicate;
    // Which taunt a 'taunt' attribute result resolves to - set below, then
    // read again outside the if/else to award it and build the result. Not
    // derived from styleIndex/REEL_3_STYLES like every other player
    // attribute: those 16 colors have no taunt-shaped equivalent, so this
    // gets its own independent random pick from LOCKABLE_TAUNT_IDS instead.
    let wonTauntId = null;
    let wonArtworkCard = null;
    let wonDeckbuilderTheme = null;
    let wonCardTheme = null;
    let wonDeckIcon = null;

    if (category === 'player') {
      reel1Index = units.length; // PLAYER_REEL_ENTRY's fixed position, see getReel1Items()
      reel1Item = PLAYER_REEL_ENTRY;
      attribute = this._pickPlayerAttribute();

      if (attribute.id === 'frame') {
        isDuplicate = inventory.hasFrame(style.id);
      } else if (attribute.id === 'hp_bar_color') {
        isDuplicate = inventory.hasHpBarColor(style.id);
      } else if (attribute.id === 'avatar_border') {
        isDuplicate = inventory.hasAvatarBorder(style.id);
      } else if (attribute.id === 'tray_color') {
        isDuplicate = inventory.hasTrayColor(style.id);
      } else if (attribute.id === 'deck_color') {
        isDuplicate = inventory.hasDeckColor(style.id);
      } else if (attribute.id === 'taunt') {
        const tauntIdx = Math.floor(Math.random() * LOCKABLE_TAUNT_IDS.length);
        wonTauntId = LOCKABLE_TAUNT_IDS[tauntIdx];
        isDuplicate = inventory.hasTaunt(wonTauntId);
      } else if (attribute.id === 'profile_artwork') {
        const artCards = getProfileArtworkCards();
        const artIdx = Math.floor(Math.random() * artCards.length);
        wonArtworkCard = artCards[artIdx];
        isDuplicate = inventory.hasProfileArtwork(wonArtworkCard.name);
      } else if (attribute.id === 'deckbuilder_card') {
        const unlockable = DECKBUILDER_THEMES.filter(t => t.surgeReward);
        const themeIdx = Math.floor(Math.random() * unlockable.length);
        wonDeckbuilderTheme = unlockable[themeIdx] || DECKBUILDER_THEMES[1];
        isDuplicate = inventory.hasDeckbuilderTheme(wonDeckbuilderTheme.id);
      } else if (attribute.id === 'card_theme') {
        const unlockable = CARD_THEMES.filter(t => t.surgeReward);
        const themeIdx = Math.floor(Math.random() * unlockable.length);
        wonCardTheme = unlockable[themeIdx] || CARD_THEMES[1];
        isDuplicate = inventory.hasCardTheme(wonCardTheme.id);
      } else if (attribute.id === 'deck_icon') {
        const unlockableIcons = DECK_ICON_PRESETS.filter(p => p.surgeReward);
        const iconIdx = Math.floor(Math.random() * unlockableIcons.length);
        wonDeckIcon = unlockableIcons[iconIdx] || unlockableIcons[0];
        isDuplicate = inventory.hasDeckIcon(wonDeckIcon.id);
      } else {
        isDuplicate = false;
      }
    } else {
      const unitSlots = this.getUnitSlots();
      const slotIdx = Math.floor(Math.random() * unitSlots.length);
      const slot = unitSlots[slotIdx];
      reel1Index = slot.unitIndex;
      reel1Item = slot.unit;
      attribute = slot.attribute;

      if (attribute.id === 'bullet_color') {
        isDuplicate = inventory.hasBulletColor(reel1Item.name, style.id);
      } else if (attribute.id === 'unit_aura') {
        isDuplicate = inventory.hasUnitAura(reel1Item.name, style.id);
      } else if (attribute.id === 'rocket_trail') {
        isDuplicate = inventory.hasRocketTrail(reel1Item.name, style.id);
      } else {
        isDuplicate = false;
      }
    }
    const reel2Index = REEL_2_ATTRIBUTES.indexOf(attribute);

    let refundAmount = 0;
    if (isDuplicate) {
      refundAmount = DUPLICATE_REFUND_FUGAZI;
      wallet.addFugazi(DUPLICATE_REFUND_FUGAZI, 'duplicate_slot_refund');
      inventory.recordSlotSpun(true);
    } else if (attribute.id === 'frame') {
      inventory.addFrame(style.id, style.name, style.hex);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'hp_bar_color') {
      inventory.addHpBarColor(style.id, style.name, style.hex);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'avatar_border') {
      inventory.addAvatarBorder(style.id, style.name, style.hex);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'tray_color') {
      inventory.addTrayColor(style.id, style.name, style.hex);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'deck_color') {
      inventory.addDeckColor(style.id, style.name, style.hex);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'taunt') {
      // Unlocked only, never auto-equipped - see the field comment on the
      // REEL_2_ATTRIBUTES 'taunt' entry above.
      inventory.unlockTaunt(wonTauntId);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'profile_artwork') {
      inventory.addProfileArtwork(wonArtworkCard.name);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'deckbuilder_card') {
      inventory.addDeckbuilderTheme(wonDeckbuilderTheme.id);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'card_theme') {
      inventory.addCardTheme(wonCardTheme.id);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'deck_icon') {
      inventory.addDeckIcon(wonDeckIcon.id, wonDeckIcon.label, wonDeckIcon.rarity);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'bullet_color') {
      inventory.addBulletColor(reel1Item.name, style.id, style.name, style.hex);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'unit_aura') {
      inventory.addUnitAura(reel1Item.name, style.id, style.name, style.hex);
      inventory.recordSlotSpun(false);
    } else if (attribute.id === 'rocket_trail') {
      inventory.addRocketTrail(reel1Item.name, style.id, style.name, style.hex);
      inventory.recordSlotSpun(false);
    }

    return {
      success: true,
      category,
      unitIndex: reel1Index,
      attributeIndex: reel2Index,
      styleIndex,
      unit: reel1Item,
      attribute,
      style,
      // Only set for a 'taunt' attribute result - the full taunt definition
      // (icon/label/text/themeColor) so the modal can render it instead of
      // the (unrelated) style swatch. Null for every other attribute.
      taunt: wonTauntId ? getTauntById(wonTauntId) : null,
      // Only set for a 'profile_artwork' attribute result - card name & image
      artwork: wonArtworkCard ? { name: wonArtworkCard.name, image: wonArtworkCard.images[0] } : null,
      // Only set for a 'deckbuilder_card' attribute result - theme definition
      deckbuilderTheme: wonDeckbuilderTheme,
      // Only set for a 'card_theme' attribute result - in-game cards theme definition
      cardTheme: wonCardTheme,
      // Only set for a 'deck_icon' attribute result - icon preset definition
      deckIcon: wonDeckIcon,
      isDuplicate,
      refundAmount,
      remainingTickets: wallet.getTickets(),
      remainingFugazi: wallet.getFugazi()
    };
  }
}

export const slotMachine = new SlotMachineManager();
export { SlotMachineManager };
